import styled from 'styled-components';
import { Card } from '@shared/ui/Card';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  & > * {
    min-width: 0;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SubTitleText = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const ErrorMessage = styled.div`
  font-size: 13px;
  color: #ef4444;
  font-weight: 600;
`;

export const FunnelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
`;

export const FunnelCard = styled(Card)<{ $accentTone?: 'green' | 'amber' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-color: ${({ $accentTone, theme }) =>
    $accentTone === 'green'
      ? 'rgba(16, 185, 129, 0.45)'
      : $accentTone === 'amber'
        ? 'rgba(245, 158, 11, 0.45)'
        : theme.colors.border};
`;

export const FunnelCardLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  opacity: 0.7;
`;

export const FunnelCardValue = styled.div`
  font-size: 22px;
  font-weight: 800;
`;

export const FunnelCardSubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const FunnelSuccessValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #10b981;
`;

export const FunnelWarningValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #f59e0b;
`;

export const FunnelCardDetail = styled.div`
  font-size: 12px;
`;

export const OverdueHighlight = styled.strong<{ $hasOverdue?: boolean }>`
  color: ${({ $hasOverdue }) => ($hasOverdue ? '#ef4444' : 'inherit')};
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 11px 12px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

export const FormBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FormTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
`;

export const Input = styled.input`
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const Select = styled.select`
  min-width: 0;
  max-width: 100%;
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

export const ProjectNameText = styled.div`
  font-weight: 700;
`;

export const CustomerNameText = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const ResponsibleText = styled.div`
  font-size: 11px;
  opacity: 0.65;
`;

export const ContractNumberText = styled.div`
  font-weight: 600;
`;

export const DocNumberText = styled.div`
  font-size: 12px;
  color: #38bdf8;
`;

export const DueDateText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const DeductionText = styled.div`
  font-size: 12px;
`;

export const DeductionSubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const EditInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ReceivedAmountText = styled.div<{ $hasReceived?: boolean }>`
  font-weight: 800;
  color: ${({ $hasReceived }) => ($hasReceived ? '#10b981' : 'inherit')};
`;

export const CollectionPctText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const ReceivableBalanceText = styled.div<{ $hasBalance?: boolean }>`
  font-weight: 700;
  color: ${({ $hasBalance }) => ($hasBalance ? '#f59e0b' : 'inherit')};
`;

export const DelayDaysText = styled.div`
  font-size: 11px;
  color: #ef4444;
  font-weight: 700;
`;

export const ActionColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const RowEditActions = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;
